// PWA service-worker registration for Distribución Canales.
//
// Adapted from the CRA template but minimal: we only register when the page
// is served over a secure origin (HTTPS) or localhost. Failures are silent —
// the app must keep working without the SW.
//
// Events fired on `window`:
//   - "dc:sw-updated" — a new worker just finished installing and is now
//     either waiting (hasController=true) or already active (hasController=false).
//   - "dc:sw-activated" — a newer worker just took control of this client
//     (via `clients.claim`). Triggered both by the SW's own postMessage and
//     by the standard `controllerchange` event.
//
// The `<UpdateBanner />` component subscribes to these and shows the "Reload"
// CTA. We deliberately don't auto-reload to avoid losing unsaved filters.

const isLocalhost = Boolean(
  typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "[::1]" ||
      /^127(?:\.[0-9]+){0,2}\.[0-9]+$/.test(window.location.hostname))
);

const dispatch = (name, detail = {}) => {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    /* old browsers — no-op */
  }
};

let bound = false;
function bindGlobalListenersOnce() {
  if (bound) return;
  bound = true;
  // `controllerchange` fires when the current client switches to a different
  // service worker (e.g. after the new one calls `clients.claim`).
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    dispatch("dc:sw-activated", { source: "controllerchange" });
  });
  // Messages from the active SW (custom protocol).
  navigator.serviceWorker.addEventListener("message", (event) => {
    const data = event?.data || {};
    if (data.type === "SW_ACTIVATED") {
      dispatch("dc:sw-activated", {
        source: "postMessage",
        version: data.version,
      });
    }
  });
}

export function register() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  // Only run on secure contexts. The preview platform always serves HTTPS,
  // so the SW will register in both dev and production builds.
  const isSecure =
    window.location.protocol === "https:" || isLocalhost;
  if (!isSecure) return;

  bindGlobalListenersOnce();

  const swUrl = `${process.env.PUBLIC_URL || ""}/service-worker.js`;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        // Notify when an update is found so the install banner can refresh.
        registration.onupdatefound = () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.onstatechange = () => {
            if (installing.state === "installed") {
              dispatch("dc:sw-updated", {
                hasController: !!navigator.serviceWorker.controller,
              });
            }
          };
        };
      })
      .catch(() => {
        /* silent — app works without SW */
      });
  });
}

export function unregister() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch(() => {});
}

/** Force the waiting worker to activate immediately (used by the banner). */
export function skipWaiting() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker
    .getRegistration()
    .then((reg) => {
      const waiting = reg?.waiting;
      if (waiting) waiting.postMessage({ type: "SKIP_WAITING" });
    })
    .catch(() => {});
}
